import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { useActivityTracking } from '../lib/activityTracking';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  Clock,
  Sparkles,
  Check,
  X,
  Loader2,
  Calendar,
  Briefcase,
  FileText,
  MessageSquare,
  Brain,
  TrendingUp,
  Timer,
  DollarSign,
  RefreshCw,
  Settings,
  BarChart3,
  Globe,
  Monitor,
  Chrome,
  Copy,
  ExternalLink,
  Plus,
  Trash2,
  Play,
  Pause,
  Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_ICONS = {
  document_view: FileText,
  document_edit: FileText,
  case_view: Briefcase,
  case_edit: Briefcase,
  communication_view: MessageSquare,
  communication_log: MessageSquare,
  email_compose: MessageSquare,
  sms_compose: MessageSquare,
  ai_exhibit_analysis: Brain,
  ai_examination_generation: Brain,
  search: FileText,
  examination_prep: Brain
};

const ACTIVITY_COLORS = {
  document_view: 'bg-blue-100 text-blue-800',
  document_edit: 'bg-blue-100 text-blue-800',
  case_view: 'bg-purple-100 text-purple-800',
  case_edit: 'bg-purple-100 text-purple-800',
  communication_view: 'bg-green-100 text-green-800',
  communication_log: 'bg-green-100 text-green-800',
  email_compose: 'bg-green-100 text-green-800',
  sms_compose: 'bg-green-100 text-green-800',
  ai_exhibit_analysis: 'bg-amber-100 text-amber-800',
  ai_examination_generation: 'bg-amber-100 text-amber-800',
  search: 'bg-slate-100 text-slate-800',
  examination_prep: 'bg-amber-100 text-amber-800'
};

export default function BillingSuggestionsPanel({ isOpen, onClose }) {
  const { token } = useAuth();
  const {
    settings,
    isTracking,
    toggleTracking,
    updateSettings,
    getBillingSuggestions,
    getActivitySummary,
    acceptSuggestion,
    rejectSuggestion
  } = useActivityTracking();

  const [activeTab, setActiveTab] = useState('suggestions');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [editForm, setEditForm] = useState({ hours: '', description: '', billable: true });
  
  // External tracking state
  const [msStatus, setMsStatus] = useState(null);
  const [extensionSettings, setExtensionSettings] = useState(null);
  const [activeCase, setActiveCase] = useState(null);
  const [cases, setCases] = useState([]);
  const [externalSummary, setExternalSummary] = useState(null);
  const [newCustomDomain, setNewCustomDomain] = useState({ domain: '', name: '' });
  const [showTokenDialog, setShowTokenDialog] = useState(false);

  // Load data when panel opens
  useEffect(() => {
    if (isOpen) {
      loadSuggestions();
      loadSummary();
      loadExternalData();
    }
  }, [isOpen]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const result = await getBillingSuggestions({ minimumMinutes: 5 });
      if (result) {
        setSuggestions(result.suggestions || []);
      }
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const result = await getActivitySummary();
      if (result) {
        setSummary(result);
      }
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadExternalData = async () => {
    if (!token) return;
    
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
      // Load Microsoft 365 status
      const msRes = await fetch(`${API_URL}/api/external-tracking/microsoft/status`, { headers });
      if (msRes.ok) setMsStatus(await msRes.json());
      
      // Load extension settings
      const extRes = await fetch(`${API_URL}/api/external-tracking/extension/settings`, { headers });
      if (extRes.ok) setExtensionSettings(await extRes.json());
      
      // Load active case
      const activeRes = await fetch(`${API_URL}/api/external-tracking/active-case`, { headers });
      if (activeRes.ok) {
        const data = await activeRes.json();
        setActiveCase(data.active_case);
      }
      
      // Load cases
      const casesRes = await fetch(`${API_URL}/api/external-tracking/cases`, { headers });
      if (casesRes.ok) setCases(await casesRes.json());
      
      // Load external summary
      const summaryRes = await fetch(`${API_URL}/api/external-tracking/activities/summary`, { headers });
      if (summaryRes.ok) setExternalSummary(await summaryRes.json());
    } catch (error) {
      console.error('Error loading external data:', error);
    }
  };

  const handleAcceptSuggestion = async (suggestion) => {
    const result = await acceptSuggestion(suggestion.id, {
      hours: editForm.hours ? parseFloat(editForm.hours) : undefined,
      description: editForm.description || undefined,
      billable: editForm.billable
    });

    if (result) {
      toast.success('Time entry created successfully');
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setEditingSuggestion(null);
    } else {
      toast.error('Failed to create time entry');
    }
  };

  const handleRejectSuggestion = async (suggestionId) => {
    const result = await rejectSuggestion(suggestionId);
    if (result) {
      toast.success('Suggestion dismissed');
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    }
  };

  const handleEditSuggestion = (suggestion) => {
    setEditingSuggestion(suggestion);
    setEditForm({
      hours: suggestion.suggested_hours.toString(),
      description: suggestion.suggested_description,
      billable: true
    });
  };

  const handleToggleTracking = async () => {
    const newState = !isTracking;
    await toggleTracking(newState);
    toast.success(newState ? 'Activity tracking enabled' : 'Activity tracking paused');
  };

  const handleConnectMicrosoft = async () => {
    try {
      const res = await fetch(`${API_URL}/api/external-tracking/microsoft/connect`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        window.open(data.auth_url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to initiate Microsoft connection');
    }
  };

  const handleDisconnectMicrosoft = async () => {
    try {
      const res = await fetch(`${API_URL}/api/external-tracking/microsoft/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMsStatus(prev => ({ ...prev, connected: false }));
        toast.success('Microsoft 365 disconnected');
      }
    } catch (error) {
      toast.error('Failed to disconnect Microsoft 365');
    }
  };

  const handleSetActiveCase = async (caseId) => {
    if (!caseId) {
      // End active case
      try {
        await fetch(`${API_URL}/api/external-tracking/active-case/end`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setActiveCase(null);
        toast.success('Clocked out of case');
      } catch (error) {
        toast.error('Failed to clock out');
      }
      return;
    }
    
    const selectedCase = cases.find(c => c.id === caseId);
    if (!selectedCase) return;
    
    try {
      const res = await fetch(`${API_URL}/api/external-tracking/active-case?case_id=${caseId}&case_name=${encodeURIComponent(selectedCase.name)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActiveCase(data.session);
        toast.success(`Clocked in to: ${selectedCase.name}`);
      }
    } catch (error) {
      toast.error('Failed to set active case');
    }
  };

  const handleAddCustomDomain = async () => {
    if (!newCustomDomain.domain || !newCustomDomain.name) {
      toast.error('Please enter both domain and name');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/external-tracking/extension/custom-domain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCustomDomain)
      });
      
      if (res.ok) {
        const data = await res.json();
        setExtensionSettings(data);
        setNewCustomDomain({ domain: '', name: '' });
        toast.success('Custom domain added');
      }
    } catch (error) {
      toast.error('Failed to add custom domain');
    }
  };

  const handleRemoveCustomDomain = async (domain) => {
    try {
      const res = await fetch(`${API_URL}/api/external-tracking/extension/custom-domain?domain=${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setExtensionSettings(data);
        toast.success('Custom domain removed');
      }
    } catch (error) {
      toast.error('Failed to remove custom domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatHours = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours.toFixed(1)}h`;
  };

  const getActivityIcon = (type) => {
    const Icon = ACTIVITY_ICONS[type] || Clock;
    return Icon;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Timer className="w-5 h-5 text-bronze" />
            AI Time Tracking & Billing
          </DialogTitle>
          <DialogDescription>
            Track time across Legal AIDynamics, Microsoft 365, and web research
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="suggestions" data-testid="suggestions-tab">
              <Sparkles className="w-4 h-4 mr-2" />
              Suggestions
            </TabsTrigger>
            <TabsTrigger value="external" data-testid="external-tab">
              <Globe className="w-4 h-4 mr-2" />
              External
            </TabsTrigger>
            <TabsTrigger value="summary" data-testid="summary-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tracking-settings-tab">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* SUGGESTIONS TAB */}
          <TabsContent value="suggestions" className="flex-1 overflow-hidden mt-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Badge variant={isTracking ? 'default' : 'secondary'} className={isTracking ? 'bg-green-600' : ''}>
                  {isTracking ? 'Tracking Active' : 'Tracking Paused'}
                </Badge>
                {suggestions.length > 0 && (
                  <Badge variant="outline">{suggestions.length} suggestions</Badge>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={loadSuggestions} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            <ScrollArea className="h-[350px] pr-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : suggestions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-medium mb-2">No Billing Suggestions</h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    Continue working and we'll analyze your activities to suggest billable time entries.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {suggestions.map((suggestion) => (
                    <Card key={suggestion.id} className="border-l-4 border-l-bronze">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium text-sm">{suggestion.case_name}</span>
                              <Badge variant="secondary" className="text-xs">{suggestion.activity_code}</Badge>
                              {suggestion.ai_generated && (
                                <Badge className="text-xs bg-bronze/10 text-bronze border-bronze/20">
                                  <Sparkles className="w-3 h-3 mr-1" />AI
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{suggestion.suggested_description}</p>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-bronze" />
                                <strong>{formatHours(suggestion.suggested_hours)}</strong>
                              </span>
                              <span className="text-muted-foreground">
                                {Math.round(suggestion.confidence_score * 100)}% confidence
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleEditSuggestion(suggestion)}>
                              <Check className="w-4 h-4 mr-1" />Accept
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejectSuggestion(suggestion.id)}>
                              <X className="w-4 h-4 mr-1" />Dismiss
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* EXTERNAL TRACKING TAB */}
          <TabsContent value="external" className="flex-1 overflow-hidden mt-0">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {/* Active Case Selector */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Play className="w-4 h-4 text-green-600" />
                      Clock In to Case
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Select a case to associate external time tracking
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <Select
                        value={activeCase?.case_id || ''}
                        onValueChange={handleSetActiveCase}
                      >
                        <SelectTrigger className="flex-1" data-testid="active-case-select">
                          <SelectValue placeholder="Select a case to clock in" />
                        </SelectTrigger>
                        <SelectContent>
                          {cases.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name} {c.case_number && `(${c.case_number})`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {activeCase && (
                        <Button variant="outline" size="sm" onClick={() => handleSetActiveCase(null)}>
                          <Pause className="w-4 h-4 mr-1" />Clock Out
                        </Button>
                      )}
                    </div>
                    {activeCase && (
                      <div className="mt-2 p-2 bg-green-50 dark:bg-green-950/20 rounded-md">
                        <p className="text-xs text-green-700 dark:text-green-300 flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          Clocked in to: <strong>{activeCase.case_name}</strong>
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Microsoft 365 Integration */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-blue-600" />
                      Microsoft 365 Integration
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Track time in Word, Excel, PowerPoint, Outlook, and Teams
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {msStatus?.connected ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950/20 rounded-md">
                          <div>
                            <p className="text-sm font-medium text-green-700 dark:text-green-300">Connected</p>
                            <p className="text-xs text-muted-foreground">{msStatus.email}</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={handleDisconnectMicrosoft}>
                            Disconnect
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {['word', 'excel', 'powerpoint', 'outlook', 'teams'].map((app) => (
                            <div key={app} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                              <span className="capitalize">{app}</span>
                              <Badge variant={msStatus[`track_${app}`] ? 'default' : 'secondary'} className="text-xs">
                                {msStatus[`track_${app}`] ? 'On' : 'Off'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div>
                        {msStatus?.configured ? (
                          <Button onClick={handleConnectMicrosoft} className="w-full" data-testid="connect-microsoft-btn">
                            <Monitor className="w-4 h-4 mr-2" />
                            Connect Microsoft 365
                          </Button>
                        ) : (
                          <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md text-xs text-amber-700 dark:text-amber-300">
                            Microsoft 365 integration requires configuration. Contact support to enable.
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Browser Extension */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Chrome className="w-4 h-4 text-amber-600" />
                      Browser Extension
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Track time on legal research sites like Westlaw, LexisNexis, PACER
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Extension Token */}
                    <div className="p-3 bg-muted/50 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs">Your Extension Token</Label>
                        <Button variant="ghost" size="sm" onClick={() => setShowTokenDialog(true)}>
                          <Copy className="w-3 h-3 mr-1" />Show
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Use this token to configure the browser extension
                      </p>
                    </div>

                    {/* Download Links */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <a href="/extension/chrome" target="_blank" download>
                          <Chrome className="w-4 h-4 mr-2" />Chrome
                        </a>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href="/extension/firefox" target="_blank" download>
                          <Globe className="w-4 h-4 mr-2" />Firefox
                        </a>
                      </Button>
                    </div>

                    {/* Domain Settings */}
                    <div className="space-y-2">
                      <Label className="text-xs">Tracked Domain Categories</Label>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span>Legal Research</span>
                          <Switch
                            checked={extensionSettings?.track_legal_research ?? true}
                            onCheckedChange={async (v) => {
                              const res = await fetch(`${API_URL}/api/external-tracking/extension/settings?track_legal_research=${v}`, {
                                method: 'PUT',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (res.ok) setExtensionSettings(await res.json());
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span>General</span>
                          <Switch
                            checked={extensionSettings?.track_general_research ?? true}
                            onCheckedChange={async (v) => {
                              const res = await fetch(`${API_URL}/api/external-tracking/extension/settings?track_general_research=${v}`, {
                                method: 'PUT',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (res.ok) setExtensionSettings(await res.json());
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span>Courts</span>
                          <Switch
                            checked={extensionSettings?.track_court_websites ?? true}
                            onCheckedChange={async (v) => {
                              const res = await fetch(`${API_URL}/api/external-tracking/extension/settings?track_court_websites=${v}`, {
                                method: 'PUT',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (res.ok) setExtensionSettings(await res.json());
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Custom Domains */}
                    <div className="space-y-2">
                      <Label className="text-xs">Custom Domains</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="domain.com"
                          value={newCustomDomain.domain}
                          onChange={(e) => setNewCustomDomain(prev => ({ ...prev, domain: e.target.value }))}
                          className="text-xs h-8"
                        />
                        <Input
                          placeholder="Display Name"
                          value={newCustomDomain.name}
                          onChange={(e) => setNewCustomDomain(prev => ({ ...prev, name: e.target.value }))}
                          className="text-xs h-8"
                        />
                        <Button size="sm" onClick={handleAddCustomDomain}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {extensionSettings?.custom_domains?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {extensionSettings.custom_domains.map((d, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {d.name}
                              <button
                                className="ml-1 hover:text-destructive"
                                onClick={() => handleRemoveCustomDomain(d.domain)}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* External Activity Summary */}
                {externalSummary && externalSummary.total_hours > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">External Activity (Last 7 Days)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="p-2 bg-muted/50 rounded">
                          <p className="text-muted-foreground">Total External</p>
                          <p className="text-lg font-bold">{externalSummary.total_hours}h</p>
                        </div>
                        <div className="p-2 bg-muted/50 rounded">
                          <p className="text-muted-foreground">By Source</p>
                          <div className="space-y-1 mt-1">
                            {Object.entries(externalSummary.by_source || {}).map(([source, hours]) => (
                              <div key={source} className="flex justify-between">
                                <span className="capitalize">{source.replace('_', ' ')}</span>
                                <span>{hours}h</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* SUMMARY TAB */}
          <TabsContent value="summary" className="flex-1 overflow-hidden mt-0">
            <ScrollArea className="h-[400px] pr-4">
              {summary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Card>
                      <CardContent className="p-3 text-center">
                        <Clock className="w-6 h-6 mx-auto text-bronze mb-1" />
                        <div className="text-xl font-bold">{summary.total_hours}h</div>
                        <div className="text-xs text-muted-foreground">In-App Time</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 text-center">
                        <Briefcase className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                        <div className="text-xl font-bold">{summary.by_case?.length || 0}</div>
                        <div className="text-xs text-muted-foreground">Cases</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 text-center">
                        <Calendar className="w-6 h-6 mx-auto text-green-600 mb-1" />
                        <div className="text-xl font-bold">{summary.total_sessions}</div>
                        <div className="text-xs text-muted-foreground">Sessions</div>
                      </CardContent>
                    </Card>
                  </div>

                  {summary.by_case?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Time by Case</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {summary.by_case.slice(0, 5).map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="truncate max-w-[200px]">{item.case_name}</span>
                              <Badge variant="secondary">{formatHours(item.hours)}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {summary.by_activity?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Time by Activity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {summary.by_activity.slice(0, 5).map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="capitalize">{item.activity_type.replace(/_/g, ' ')}</span>
                              <Badge variant="secondary">{formatHours(item.hours)}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="flex-1 overflow-hidden mt-0">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">In-App Activity Tracking</h4>
                        <p className="text-sm text-muted-foreground">Track time within Legal AIDynamics</p>
                      </div>
                      <Switch checked={isTracking} onCheckedChange={handleToggleTracking} data-testid="tracking-toggle" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Track These Activities</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { key: 'track_document_activities', label: 'Documents', icon: FileText },
                      { key: 'track_case_activities', label: 'Cases', icon: Briefcase },
                      { key: 'track_communication_activities', label: 'Communications', icon: MessageSquare },
                      { key: 'track_ai_activities', label: 'AI Analysis', icon: Brain },
                      { key: 'track_billing_activities', label: 'Billing', icon: DollarSign }
                    ].map(({ key, label, icon: Icon }) => (
                      <div key={key} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{label}</span>
                        </div>
                        <Switch
                          checked={settings?.[key] ?? true}
                          onCheckedChange={(v) => updateSettings({ [key]: v })}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Edit Suggestion Dialog */}
      {editingSuggestion && (
        <Dialog open={!!editingSuggestion} onOpenChange={() => setEditingSuggestion(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Time Entry</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Case</Label>
                <div className="p-2 bg-muted rounded-md text-sm">{editingSuggestion.case_name}</div>
              </div>
              <div>
                <Label>Hours</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editForm.hours}
                  onChange={(e) => setEditForm(prev => ({ ...prev, hours: e.target.value }))}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Billable</Label>
                <Switch
                  checked={editForm.billable}
                  onCheckedChange={(v) => setEditForm(prev => ({ ...prev, billable: v }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingSuggestion(null)}>Cancel</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleAcceptSuggestion(editingSuggestion)}>
                <Check className="w-4 h-4 mr-2" />Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Token Dialog */}
      <Dialog open={showTokenDialog} onOpenChange={setShowTokenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Browser Extension Token</DialogTitle>
            <DialogDescription>
              Copy this token and paste it in the browser extension popup
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-md">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">API URL</Label>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(API_URL)}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <code className="text-xs break-all">{API_URL}</code>
            </div>
            <div className="p-3 bg-muted rounded-md">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Extension Token</Label>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(extensionSettings?.extension_token || '')}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <code className="text-xs break-all">{extensionSettings?.extension_token || 'Loading...'}</code>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowTokenDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
